import React from 'react';
import { Controller } from 'react-hook-form';
import { Checkbox, FormControlLabel, FormGroup, FormHelperText } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import { StatusManager } from 'common/components/StatusManager';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  DrawerContentContainer,
  useStyles
} from 'UI/components/templates/FPDrawer/FPDrawer.styled';
import { DateFormats } from 'UI/constants/defaults';
import { UIStatus } from 'UI/constants/status';
import { REQUIRED_VALIDATION, toLocalTime } from 'UI/utils';
import { Selectors } from 'UI/utils/renderers';

import FPButtonBuilder from '../FPButtonBuilder';

import { useAlertClasses } from './styled';
import { VERIFY_URGENCY_FIELDS } from './VerifyUrgencyDialog.constants';
import useVerifyUrgency from './VerifyUrgencyDialog.hooks';

const ITEMS_REQUIRED_VALIDATION = {
  validate(items) {
    return (items && items.length > 0) || REQUIRED_VALIDATION.required;
  }
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {Function} props.onClose
 * @param {Function} props.onSaveSucceeded
 */
export const VerifyUrgencyDialog = ({ open, entityType, entityId, onClose, onSaveSucceeded }) => {
  const { actionsTakenOptions, handleSave, form, uiState, verification } = useVerifyUrgency({
    entityType,
    entityId,
    onSaveSucceeded
  });

  const { handleSubmit, control, setValue, errors } = form;

  const getVerificationDateAndUser = info =>
    `Updated at: ${toLocalTime(info.updated_at).format(DateFormats.SimpleDateTimeHyphen)} by ${
      info.user.name
    }`;

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose,
    isSaveDisabled: uiState === UIStatus.Saving,
    isSaving: uiState === UIStatus.Saving
  });

  const classes = useStyles({ hasPaddingContent: true });

  const alertClasses = useAlertClasses();

  return (
    <FPDrawer
      FPDrawerProps={{ open }}
      onClose={onClose}
      ContentWrapper="form"
      hasPaddingContent={false}
      ContentWrapperProps={{ onSubmit: handleSubmit(handleSave) }}
      CardHeaderProps={{ title: strings.inventory.urgencyVerification.title }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <Alert classes={alertClasses} severity="info">
        {strings.inventory.urgencyVerification.description}
      </Alert>

      <StatusManager status={uiState === UIStatus.Loading ? UIStatus.Loading : UIStatus.Success}>
        <DrawerContentContainer classes={{ root: classes.root }}>
          <Typography variant="subtitle1">
            {strings.inventory.urgencyVerification.subtitle}:
          </Typography>
          <FormGroup row>
            <Controller
              as={
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      onChange={e => {
                        setValue(VERIFY_URGENCY_FIELDS.IsVerified.key, e.target.checked, true);
                      }}
                    />
                  }
                  label={strings.inventory.urgencyVerification.fields.verified}
                />
              }
              name={VERIFY_URGENCY_FIELDS.IsVerified.key}
              control={control}
              rules={REQUIRED_VALIDATION}
            />
          </FormGroup>
          <FormHelperText error margin="dense" className="MuiFormHelperText-contained">
            {errors[VERIFY_URGENCY_FIELDS.IsVerified.key] &&
              errors[VERIFY_URGENCY_FIELDS.IsVerified.key].message}
          </FormHelperText>
          <Controller
            as={
              <AutocompleteSelect
                placeholder={strings.inventory.urgencyVerification.fields.actions}
                label={strings.inventory.urgencyVerification.fields.actions}
                error={!!errors[VERIFY_URGENCY_FIELDS.ActionsTaken.key]}
                errorText={
                  errors[VERIFY_URGENCY_FIELDS.ActionsTaken.key] &&
                  errors[VERIFY_URGENCY_FIELDS.ActionsTaken.key].message
                }
                defaultOptions={actionsTakenOptions}
                multiple
                getOptionSelected={Selectors.byId}
              />
            }
            onChange={([_, value]) => value}
            name={VERIFY_URGENCY_FIELDS.ActionsTaken.key}
            onChangeName="onSelect"
            valueName="selectedValue"
            control={control}
            rules={ITEMS_REQUIRED_VALIDATION}
          />
          <Controller
            as={
              <TextBox
                label={strings.inventory.urgencyVerification.fields.details}
                name={VERIFY_URGENCY_FIELDS.MoreDetails.key}
                multiline
              />
            }
            control={control}
            onChange={([_, value]) => value}
            name={VERIFY_URGENCY_FIELDS.MoreDetails.key}
          />
          {verification && (
            <FormHelperText>{getVerificationDateAndUser(verification)}</FormHelperText>
          )}
        </DrawerContentContainer>
      </StatusManager>
    </FPDrawer>
  );
};
